import React, { useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import OracleInterfaceV2 from './components/OracleInterfaceV2';
import OracleExplorer from './components/OracleExplorer';
import FactSuggestion from './components/FactSuggestion';
import GovernanceVoting from './components/GovernanceVoting';
import PendingFactsApproval from './components/PendingFactsApproval';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import { Providers } from './providers';
import '@rainbow-me/rainbowkit/styles.css';

// Suppress Chrome extension errors in console and runtime
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args.join(' ');
  if (
    message.includes('chrome.runtime.sendMessage') ||
    message.includes('chrome-extension://') ||
    message.includes('Error in invocation of runtime.sendMessage') ||
    message.includes('i is not a function') || // WalletConnect localStorage error
    message.includes('TypeError: i is not a function') || // WalletConnect error
    message.includes('manifest.json') || // Manifest parsing error (non-critical)
    message.includes('Manifest: Line: 1') // Manifest parsing error
  ) {
    return; // Suppress these specific errors
  }
  originalConsoleError.apply(console, args);
};

// Suppress runtime errors from Chrome extensions and manifest
window.addEventListener('error', (event) => {
  const message = event.message || '';
  if (
    message.includes('chrome.runtime.sendMessage') ||
    message.includes('chrome-extension://') ||
    message.includes('Error in invocation of runtime.sendMessage') ||
    message.includes('manifest.json') || // Manifest parsing error (non-critical)
    (event.filename && event.filename.includes('manifest.json'))
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Suppress unhandled promise rejections from Chrome extensions and WalletConnect
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || event.reason || '';
  if (
    message.includes('chrome.runtime.sendMessage') ||
    message.includes('chrome-extension://') ||
    message.includes('Error in invocation of runtime.sendMessage') ||
    message.includes('i is not a function') || // WalletConnect localStorage error
    (typeof message === 'string' && message.includes('TypeError: i is not a function'))
  ) {
    event.preventDefault();
    event.stopPropagation();
  }
});

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6366f1',
    },
    secondary: {
      main: '#8b5cf6',
    },
    background: {
      default: '#0f0f23',
      paper: '#1a1a2e',
    },
  },
  typography: {
    fontFamily: "'Courier Prime', 'Courier New', monospace",
    allVariants: {
      fontFamily: "'Courier Prime', 'Courier New', monospace",
    },
  },
});

function App() {
  const [currentPage, setCurrentPage] = useState('chat');
  const [selectedOracleForSuggestion, setSelectedOracleForSuggestion] = useState<string>('');

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'chat':
        return <OracleInterfaceV2 />;
      case 'explorer':
        return <OracleExplorer onNavigateToSuggest={(oracleName) => {
          setSelectedOracleForSuggestion(oracleName);
          setCurrentPage('suggest');
        }} />;
      case 'suggest':
        return <FactSuggestion preselectedOracle={selectedOracleForSuggestion} />;
      case 'governance':
        return <GovernanceVoting />;
      case 'approval':
        return <PendingFactsApproval />;
      default:
        return <OracleInterfaceV2 />;
    }
  };

  return (
    <ErrorBoundary>
      <Providers>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
          {renderCurrentPage()}
        </ThemeProvider>
      </Providers>
    </ErrorBoundary>
  );
}

export default App;